export type Expression =
  | LogicalExpression
  | UnaryLogicalExpression
  | LiteralExpression
  | DataObjectExpression
  | DataFieldExpression
  | DataFieldPathExpression
  | DataVariableExpression
  | FunctionExpression
  | CaseExpression
  | UnaryArithmeticExpression
  | BinaryArithmeticExpression
  | ComparisonExpression
  | SetComparisonExpression
  | RangeComparisonExpression
  | UnaryComparisonExpression
  | StringComparisonExpression
  | leftOperand
  | conditionOperand
  | resultOperand
  | ExpressionArray

export interface ExpressionWrapper {
  expression: Expression;
}

type ReturnType = 'string' | 'number' | 'integer' | 'any';
export interface SqlFunction {
  name: string;
  parameters: Array<{
    name: string;
    type: string;
    variadic?: boolean;
    optional?: boolean;
  }>;
  returnType: string;
  description: string;
}

export interface LiteralExpression {
  dataType: 'LogicalTruth' | 'BinaryBit' | 'IntegerNumber' | 'FloatingPointNumber' | 'CharacterString' | 'TextObject' | 'InstanceObject' | 'IntervalObject' | 'GeometryObject' | 'GeographyObject' | 'BinaryStructured' | 'BinaryUnstructured';
  literalValue: string;
  placeHolder?: string;
}

export interface ExpressionArray {
  expressions: Expression[];
}


export interface DataObjectExpression {
  dataObjectName: string;
}

export interface DataFieldExpression {
  dataFieldName: string;
}

export interface DataFieldPathExpression {
  dataFieldPath: string;
  dataType?: string;
}

export interface DataVariableExpression {
  dataVariableName: string;
}

export interface LogicalExpression {
  operator: 'AND' | 'OR' | 'NOT';
  operands: ExpressionWrapper[];
}
export interface UnaryLogicalExpression {
  operator: 'NOT';
  operand: ExpressionWrapper;
}
export interface FunctionExpression {
  fxName: string;
  fxArgs: ExpressionWrapper[];
  inputType?: string;
}

export interface CaseExpression {
  operator: 'CASE';
  branchOperand: {
    conditionOperand: {
      operator: 'WHEN';
      operand: Expression;
    };
    resultOperand: {
      operator: 'THEN';
      operand: Expression;
    };
  }[];
  defaultOperand: Expression;
}

export interface conditionOperand{
  operator: 'WHEN';
  operand: Expression;
}



export interface resultOperand{
  operator: 'THEN';
  operand: Expression;
}

export interface UnaryArithmeticExpression {
  operator: '+' | '-';
  operand: ExpressionWrapper;
}

export interface BinaryArithmeticExpression {
  operator: '+' | '-' | '*' | '/';
  operands: Expression[];
}

export interface leftOperand{
  operator: '+' | '-' | '*' | '/' | '%';
  leftOperand: ExpressionWrapper;
  rightOperand: ExpressionWrapper;
}
export interface ComparisonExpression {
  operator: '=' | '==' | '<>' | '<' | '<=' | '>' | '>=' | '!=' | '!<' | '!>' | '!<=' | '|';
  leftOperand: ExpressionWrapper;
  rightOperand: ExpressionWrapper;
}



export interface SetComparisonExpression {
  operator: 'IN' | 'NOT IN' | '= ANY OF' | '<> ANY OF' | '< ANY OF' | '<= ANY OF' | '> ANY OF' | '>= ANY OF' | '!= ANY OF' | '!< ANY OF' | '!> ANY OF' | '!<= ANY OF' | '!>= ANY OF' | '= ALL OF' | '<> ALL OF' | '< ALL OF' | '<= ALL OF' | '> ALL OF' | '>= ALL OF' | '!= ALL OF' | '!< ALL OF' | '!> ALL OF' | '!<= ALL OF' | '!>= ALL OF';
  leftOperand: ExpressionWrapper;
  rightOperands: ExpressionWrapper[];
}

export interface RangeComparisonExpression {
  operator: 'BETWEEN' | 'NOT BETWEEN';
  leftOperand: ExpressionWrapper;
  rightOperands: [ExpressionWrapper, ExpressionWrapper];
}



export interface UnaryComparisonExpression {
  operator: 'IS NULL' | 'IS NOT NULL' | 'IS TRUE' | 'IS FALSE' | 'IS UNKNOWN';
  operand: ExpressionWrapper;
}

export interface StringComparisonExpression {
  operator: 'LIKE' | 'NOT LIKE';
  leftOperand: ExpressionWrapper;
  rightOperands: ExpressionWrapper[];
}

// {
//   "operator": "NOT",
//   "operand": {
//     "operator": "=",
//     "leftOperand": {
//       "literalValue": ""
//     },
//     "rightOperand": {
//       "literalValue": ""
//     }
//   }
// }



export function isUnaryLogicalExpression(expr: Expression): expr is UnaryLogicalExpression {
  return expr !== null && typeof expr === 'object' && 'operator' in expr && expr.operator === 'NOT' && 'operand' in expr;
}

export function isLogicalExpression(expr: Expression): expr is LogicalExpression {
  return 'operator' in expr && ['AND', 'OR'].includes(expr.operator) && 'operands' in expr && Array.isArray(expr.operands);
}
export function isLiteralExpression(expr: Expression): expr is LiteralExpression {
  return  'literalValue' in expr;
}

export function isExpressionArray(expr: Expression) {
  return Array.isArray(expr);
}
// is ArrayExpression it may contain leteral or datafieldpath
export function isArrayExpression(expr: Expression) {
  return 'literalValue' in expr && Array.isArray((expr as any).literalValue);
}

export function isDataObjectExpression(expr: Expression): expr is DataObjectExpression {
  return 'dataObjectName' in expr;
}

export function isDataFieldExpression(expr: Expression): expr is DataFieldExpression {
  return 'dataFieldName' in expr;
}

export function isDataFieldPathExpression(expr: any): expr is DataFieldPathExpression {
  console.log("isDataFieldPathExpression",JSON.stringify(expr),'dataFieldPath' in expr);
  return 'dataFieldPath' in expr;
}

export function isDataVariableExpression(expr: Expression): expr is DataVariableExpression {
  return 'dataVariableName' in expr;
}

export function isFunctionExpression(expr: Expression): expr is FunctionExpression {
  return 'fxName' in expr && 'fxArgs' in expr;
}

export function isCaseExpression(expr: Expression): expr is CaseExpression {
  return 'operator' in expr && expr.operator === 'CASE' && 'branchOperand' in expr && 'defaultOperand' in expr;
}

export function isUnaryArithmeticExpression(expr: Expression): expr is UnaryArithmeticExpression {
  return 'operator' in expr && (expr.operator === '+' || expr.operator === '-') && 'operand' in expr;
}

export function isBinaryArithmeticExpression(expr: any): expr is BinaryArithmeticExpression {
  return expr && 
    typeof expr === 'object' && 
    'operator' in expr &&
    'operands' in expr &&
    Array.isArray(expr.operands);
}

export function isComparisonExpression(expr: Expression): expr is ComparisonExpression {
  return 'operator' in expr && ['=','==', '<>', '<', '<=', '>', '>=', '!=', '!<', '!>','!<='].includes(expr.operator) && 'leftOperand' in expr && 'rightOperand' in expr;
}

export function isSetComparisonExpression(expr: Expression): expr is SetComparisonExpression {
  console.log("isSetComparisonExpression",JSON.stringify(expr));
  return 'operator' in expr && ['IN', 'NOT IN', '= ANY OF', '<> ANY OF', '< ANY OF', '<= ANY OF', '> ANY OF', '>= ANY OF', '!= ANY OF', '!< ANY OF', '!> ANY OF', '!<= ANY OF', '!>= ANY OF', '= ALL OF', '<> ALL OF', '< ALL OF', '<= ALL OF', '> ALL OF', '>= ALL OF', '!= ALL OF', '!< ALL OF', '!> ALL OF', '!<= ALL OF', '!>= ALL OF'].includes(expr.operator) && 'leftOperand' in expr && 'rightOperands' in expr;
}
export function isSetComparisonOperator(operator: string): boolean {
  const setComparisonOperators = [
    'IN', 'NOT IN',
    '= ANY OF', '<> ANY OF', '< ANY OF', '<= ANY OF', 
    '> ANY OF', '>= ANY OF', '!= ANY OF', 
    '!< ANY OF', '!> ANY OF', '!<= ANY OF', '!>= ANY OF',
    '= ALL OF', '<> ALL OF', '< ALL OF', '<= ALL OF', 
    '> ALL OF', '>= ALL OF', '!= ALL OF', 
    '!< ALL OF', '!> ALL OF', '!<= ALL OF', '!>= ALL OF'
  ];

  return setComparisonOperators.includes(operator);
}

export function isUnaryComparisonOperator(operator: string): boolean {
  return ['IS NULL', 'IS NOT NULL', 'IS TRUE', 'IS FALSE', 'IS UNKNOWN'].includes(operator);
}

export function isRangeComparisonExpression(expr: Expression): expr is RangeComparisonExpression {
  return 'operator' in expr && ['BETWEEN', 'NOT BETWEEN'].includes(expr.operator) && 'leftOperand' in expr && 'rightOperands' in expr && Array.isArray(expr.rightOperands) && expr.rightOperands.length === 2;
}

export function isRangeComparisonOperator (operator: string): boolean  {
  return ['BETWEEN', 'NOT BETWEEN'].includes(operator);
}

export function isUnaryComparisonExpression(expr: Expression): expr is UnaryComparisonExpression {
  return 'operator' in expr && ['IS NULL', 'IS NOT NULL', 'IS TRUE', 'IS FALSE', 'IS UNKNOWN'].includes(expr.operator) && 'operand' in expr;
}

export function isStringComparisonExpression(expr: Expression): expr is StringComparisonExpression {
  return 'operator' in expr && ['LIKE', 'NOT LIKE'].includes(expr.operator) && 'leftOperand' in expr && 'rightOperands' in expr;
}

export function isLeftOperandOnly(expr: Expression): expr is leftOperand {
  return 'leftOperand' in expr && !('rightOperand' in expr);
}

export function isConditionOperand(expr: Expression): expr is conditionOperand {
  return 'conditionOperand' in expr && 'WHEN' in expr;
}
